import type { IInitialProduct } from '@/services/serverless/integrations/AWS/AWSCartService';
import { isNullish } from '@/utils/null-utils';
import type { IGiftCardRemoteLineItem } from '@/services/serverless/integrations/AWS/AWSCartService/data-structures/IRemoteLineItem';
import type { IGiftCardInitialProduct } from '@/services/serverless/integrations/AWS/AWSCartService/data-structures/IInitialProduct';
import type { DTO } from '@/type-utils';
import type { IProduct } from '../Product';
import type IGiftCardProduct from '../Product/IGiftCardProduct';
import type ICart from './ICart';
import LineItemType from './LineItem/LineItemType';

/**
 * Creates the initial product line item from a gift card product.
 * @param product - The gift card product model.
 * @returns - The transformed {@link IInitialProduct}.
 */
export const getInitialProductFromGiftCardProductModel = (
  product: IGiftCardProduct
): IGiftCardInitialProduct => ({
  pid: product.id,
  sku: product.sku,
  quantity: 1,
  isGiftCard: true,
  ...getGiftCardRemoteLineItemFromProduct(product)
});

/**
 * Obtain the remote line item properties of a gift card
 * from a product.
 * @param product - The @{@link IProduct}.
 * @returns - The {@link IGiftCardRemoteLineItem}.
 */
export const getGiftCardRemoteLineItemFromProduct = (
  product: IGiftCardProduct
): IGiftCardRemoteLineItem => ({
  giftCardRecipientEmail: product.recipientEmail,
  giftCardRecipientName: product.recipientName,
  giftCardSenderName: product.senderName,
  giftCardImageUrl: product.imageUrl,
  giftMessage: product.message,
  price: isNullish(product.price) ? 0 : Number(product.price.price.amount)
});

/**
 * Gets the type of the given cart; `gift-card` carts can only contain a single
 * gift card, whereas `standard` carts can contain any purchasable items.
 *
 * **Note**: A cart can hypothetically change its type during its lifetime,
 * but in practice this should not happen. However, ensure the cart is "ready"
 * before relying on this property.
 *
 * @param cart - The cart to get the type of.
 * @returns The type of the cart.
 */
export function getCartType(
  cart: DTO<ICart> | ICart
): 'gift-card' | 'standard' {
  const { items } = cart;
  if (items.length === 1 && items[0].type === LineItemType.GiftCard) {
    return 'gift-card';
  }
  return 'standard';
}
